import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { useEmailComposerContext } from '../../../../../models/Email';
import { useGetSurveyById } from '../../../../../queries';
import { getNpsQuestionStats } from '../../../../../surveys/utils/scaleQuestions';
import { ComposeCustomSurveyFollowUpEmailViewModel } from '../../../../../viewmodels/AppViewModels';
import { grayIconFill } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { PopoverType, TinyPopover } from '../../../TinyPopover';
import { InfoIcon } from '../../../svgs/icons/InfoIcon';
import {
	FollowUpRecipientDiscriminator,
	IFollowUpRecipientDiscriminatorOption,
} from '../FollowUpRecipientDiscriminator';
import { styleSheet } from './styles';

interface IProps {
	styles?: StyleDeclarationValue[];
}

export function NpsSurveyFollowUpOptions({ styles = [] }: IProps) {
	const { emailComposer } = useEmailComposerContext<
		Api.ISurveyFollowUpOptions,
		Api.CustomSurveyReportViewModel,
		ComposeCustomSurveyFollowUpEmailViewModel
	>();

	const surveyId = emailComposer.followUpSource.survey.id;
	const { data, isPending } = useGetSurveyById<Api.ICustomSurvey>({ surveyId });

	const options = React.useMemo<IFollowUpRecipientDiscriminatorOption<Api.NpsCategory | null>[]>(() => {
		const stats = getNpsQuestionStats(data);
		if (!stats) {
			return [];
		}
		const promoters = stats.promoters || 0;
		const passives = stats.passives || 0;
		const detractors = stats.detractors || 0;
		return [
			{
				count: promoters + passives + detractors,
				id: 'all',
				representedObject: null,
				text: 'All recipients',
			},
			{
				count: promoters,
				disabled: !promoters,
				id: `promoters-${promoters}`,
				representedObject: Api.NpsCategory.Promoter,
				text: 'Promoters',
			},
			{
				count: passives,
				disabled: !passives,
				id: `passives-${passives}`,
				representedObject: Api.NpsCategory.Passive,
				text: 'Passives',
			},
			{
				count: detractors,
				disabled: !detractors,
				id: `detractors-${detractors}`,
				representedObject: Api.NpsCategory.Detractor,
				text: 'Detractors',
			},
		];
	}, [data]);

	const [selectedOptions, setSelectedOptions] = React.useState<
		IFollowUpRecipientDiscriminatorOption<Api.NpsCategory | null>[]
	>([]);

	const onSelectedOptionsChanged = React.useCallback(
		(o: IFollowUpRecipientDiscriminatorOption<Api.NpsCategory | null>[]) => {
			const selected = selectedOptions || [];
			let nextSelected = o;
			const indexOfSelectAll = nextSelected.findIndex(x => x.id === 'all');
			if (indexOfSelectAll >= 0) {
				if (!selected.find(x => x.id === 'all')) {
					nextSelected = options.filter(x => !x.disabled);
				} else {
					nextSelected.splice(indexOfSelectAll, 1);
				}
			} else {
				if (selected.find(x => x.id === 'all')) {
					nextSelected = [];
				} else if (nextSelected.length === options.filter(x => !x.disabled).length - 1) {
					nextSelected = options.filter(x => !x.disabled);
				}
			}

			const isAll = nextSelected.some(x => x.id === 'all');
			const categories: Api.NpsCategory[] = isAll
				? []
				: nextSelected
						.filter(x => x.id !== 'all' && x.representedObject != null)
						.map(x => x.representedObject as Api.NpsCategory);

			if (emailComposer.emailMessage.options.followUp) {
				const filter: Api.ICustomSurveyResponseFilterRequest = {
					...(emailComposer.emailMessage.options.followUp.surveyResponseFilter || {}),
					surveyId,
					_type: 'CustomSurveyResponseFilterRequest',
				};
				// Omit for "All recipients": an empty band matches no one, omission means everyone.
				if (categories.length) {
					filter.npsCategories = categories;
				} else {
					delete filter.npsCategories;
				}
				emailComposer.emailMessage.options.followUp.surveyResponseFilter = filter;
			}
			emailComposer.selectedFollowUpNpsCategories = categories;
			setSelectedOptions(nextSelected);
		},
		[emailComposer, options, selectedOptions, surveyId]
	);

	return (
		<div className={`${css(styleSheet.container, ...styles)}`}>
			<h4 className={css(styleSheet.header)}>
				Select Recipients to Follow-Up
				<TinyPopover
					anchor={
						<figure className={css(baseStyleSheet.flex, baseStyleSheet.itemsCenter)}>
							<InfoIcon fillColor={grayIconFill} />
						</figure>
					}
					anchorStyles={[styleSheet.infoPopoverAnchor]}
					dismissOnOutsideAction={true}
					toggleOnHover={true}
					autoCloseOtherPopoversOnHover={true}
					type={PopoverType.background}
					placement={['right', 'top', 'bottom']}
				>
					<p className={css(styleSheet.infoPopoverContent)}>
						Counts reflect all-time responses. Promoters scored 9–10, Passives 7–8, Detractors 0–6.
					</p>
				</TinyPopover>
			</h4>
			<div className={css(styleSheet.content)}>
				{isPending ? (
					<LoadingSpinner type='small' />
				) : (
					<FollowUpRecipientDiscriminator
						multiSelect={true}
						onSelectedOptionsChanged={onSelectedOptionsChanged}
						options={options}
						selectedOptions={selectedOptions}
					/>
				)}
			</div>
		</div>
	);
}
